/**
 * Platform-wide usage: summary figures over the period (credits billed,
 * active organisations, and -- once the platform figure has settled -- the
 * platform's own provider cost and margin), then one row per tenant (or per
 * day and tenant), with margin and margin % (platform design §18.7).
 *
 * Column order matters: calls first (the activity signal), then
 * organisation credits (price), then the provider figures (cost) grouped
 * together, margin and margin % last on the right, because margin is what
 * an operator scans for and its two inputs are passed on the way to it.
 * Token counts sit inside the cost group -- they are a cost figure
 * (platform design §10.1), not a price one.
 *
 * The period is a fixed preset and is never persisted: a remembered period
 * is how an operator reads last month's numbers believing they are this
 * month's, and the period is one control, not a preference.
 *
 * **The grouping a row belongs to is read off the row itself, never off the
 * `groupBy` request state.** `useOperatorSpend`'s `groupBy` is only ever a
 * request parameter; whether the *response* is actually day-level is
 * decided per platform design §18.7's own contract (`core`'s
 * `usage.operator_spend`: "day on that [tenant-summed] row is `until`, the
 * range's own end") -- a row whose
 * `day` differs from the query's own end date, or a tenant that appears in
 * more than one row, is real day-level data regardless of what was asked
 * for. This is what keeps a stale or mismatched response from being
 * rendered as if it agreed with the request that produced it
 * (`[request-shape-is-a-contract-too]`).
 *
 * **A registered tenant with no row for the period is shown, not dropped**:
 * `useOpsTenants` names every registered tenant, and one with no matching
 * spend row renders "sem leitura" across every figure -- an absent reading,
 * never a fabricated zero (platform design §5.4's rule carried here). The
 * tenant list only enriches the answer: while it is pending or has failed
 * the spend table still renders, without filler rows or a registered count.
 *
 * **No per-row provider figure and no per-row margin is ever summed across
 * organisations** (platform design §18.7: a call shared by *n* payers is
 * counted *n* times by such a sum), so the credits billed total is this
 * component's own sum over the loaded rows, never a second endpoint.
 * Active-vs-registered organisations is a count over `useOpsTenants` against
 * the same rows. The platform cost and margin tiles are the one exception
 * §18.7's own amendment allows: they read `core`'s dedicated once-per-call
 * figure (`usePlatformMargin`), never a sum over this screen's own
 * rows, and render nothing at all while that figure is loading, failed or
 * genuinely absent for the period -- never a dash standing in for a number
 * nobody has yet confirmed.
 */

import { useMemo, type ReactNode } from 'react';

import { Page, PageHeader, StatCard, StatGrid } from '../../chrome/page';
import { useViewSearch } from '../../chrome/viewSearch';
import { opsTitles } from '../shell/titles';
import { EmptyState, ErrorState } from '../../chrome/state';
import { DataTable, FilterBar, type ColumnDef } from '../../chrome/table';
import { defineCatalogue, getLocale, type Locale } from '../../i18n';
import { integer } from '../../i18n/format';
import {
  useOperatorSpend,
  useOpsTenants,
  usePlatformMargin,
  type UsageGroupBy,
} from '../api/queries';
import type { OperatorSpendRow, OpsTenantRow, PlatformMargin } from '../api/schemas';
import { Badge } from '../../ui/badge';
import { formatCalendarDay } from '../lib/date';
import { CostGroup, Credits, Margin, MarginPercent, marginPercent } from '../lib/units';
import { periodToRange, UsageControls, type UsagePeriod } from './UsageControls';

const catalogue = defineCatalogue(
  {
    'filters.label': 'Filtrar uso',
    'header.day': 'Dia',
    'header.tenant': 'Organização',
    'header.calls': 'Chamadas',
    'header.tenantCredits': 'Cobrado',
    'header.cost': 'Custo do provedor',
    'header.margin': 'Margem',
    'header.marginPercent': 'Margem %',
    'summary.tenantCredits.label': 'Cobrado em {days} d',
    'summary.activeTenants.label': 'Organizações ativas',
    'summary.activeTenants.hint': 'de {total} registradas',
    'summary.platformCost.label': 'Custo do provedor',
    'summary.platformMargin.label': 'Margem',
    'summary.platform.hint': 'Toda a plataforma, no período',
    'filters.period': '{from} – {to}',
    'tokens.input': 'Entrada',
    'tokens.output': 'Saída',
    noReading: 'sem leitura',
    empty: 'Nenhum uso neste período.',
    'table.caption': 'Uso da plataforma no período selecionado',
  },
  {
    'filters.label': 'Filter usage',
    'header.day': 'Day',
    'header.tenant': 'Organisation',
    'header.calls': 'Calls',
    'header.tenantCredits': 'Billed',
    'header.cost': 'Provider cost',
    'header.margin': 'Margin',
    'header.marginPercent': 'Margin %',
    'summary.tenantCredits.label': 'Billed over {days} d',
    'summary.activeTenants.label': 'Active organisations',
    'summary.activeTenants.hint': 'of {total} registered',
    'summary.platformCost.label': 'Provider cost',
    'summary.platformMargin.label': 'Margin',
    'summary.platform.hint': 'Whole platform, for the period',
    'filters.period': '{from} – {to}',
    'tokens.input': 'Input',
    'tokens.output': 'Output',
    noReading: 'no reading',
    empty: 'No usage in this period.',
    'table.caption': 'Platform usage for the selected period',
  },
);
const t = catalogue.t;

/** Whether `rows` actually carries day-level data, read from the rows
 * themselves rather than from the `groupBy` this screen asked for
 * (`[request-shape-is-a-contract-too]`). Two independent signals, either
 * one enough: a tenant appearing more than once can only happen under
 * day-level grouping, and a `day` short of the period's own end is a real
 * date -- `core`'s own contract is that a tenant-summed row's `day` is
 * always the range's end. */
function hasDayLevelData(rows: OperatorSpendRow[], periodEnd: string): boolean {
  const seenTenants = new Set<string>();
  for (const row of rows) {
    if (seenTenants.has(row.tenant_id)) return true;
    seenTenants.add(row.tenant_id);
    if (row.day !== periodEnd) return true;
  }
  return false;
}

/** One real spend row, or a registered tenant absent from the period's
 * rows -- rendered as an absent reading rather than a silent drop. */
type UsageRow =
  | { kind: 'data'; row: OperatorSpendRow }
  | { kind: 'no-reading'; tenantId: string; tenantName: string };

/** `useOpsTenants` names every registered tenant; a tenant with no row in
 * `rows` for the period gets one `no-reading` row rather than vanishing
 * from the table -- only meaningful once the table is one row per tenant
 * (`dayLevel` false); day-level data has no single "the" row a missing
 * tenant would occupy, so no filler is invented for it. `tenants` is `null`
 * until the tenant list has arrived, and then no filler is invented either.
 * An answer with no rows at all is "no usage in the period", which the empty
 * state says once; a filler per registered tenant would say it again. */
function usageRows(
  rows: OperatorSpendRow[],
  tenants: OpsTenantRow[] | null,
  dayLevel: boolean,
): UsageRow[] {
  const data: UsageRow[] = rows.map((row) => ({ kind: 'data', row }));
  if (dayLevel || tenants === null || rows.length === 0) return data;
  const covered = new Set(rows.map((row) => row.tenant_id));
  const missing: UsageRow[] = tenants
    .filter((tenant) => !covered.has(tenant.tenant_id))
    .map((tenant) => ({ kind: 'no-reading', tenantId: tenant.tenant_id, tenantName: tenant.name }));
  return [...data, ...missing];
}

function columns(
  locale: Locale,
  showDay: boolean,
  showTokens: boolean,
): ColumnDef<UsageRow, unknown>[] {
  const dayColumn: ColumnDef<UsageRow, unknown> = {
    id: 'day',
    header: t('header.day'),
    accessorFn: (r) => (r.kind === 'data' ? r.row.day : null),
    cell: ({ row }) => {
      const r = row.original;
      return (
        // Anchors `screen.findByTestId('usage-row')` to one cell rather than the `<tr>`
        // DataTable owns; any cell would do, this one is simply the first.
        <span data-testid="usage-row">
          {r.kind === 'data' ? formatCalendarDay(r.row.day, locale) : t('noReading')}
        </span>
      );
    },
  };
  const tenantColumn: ColumnDef<UsageRow, unknown> = {
    id: 'tenant',
    header: t('header.tenant'),
    accessorFn: (r) => (r.kind === 'data' ? r.row.tenant_name : r.tenantName),
    cell: ({ row }) => {
      const r = row.original;
      const name = r.kind === 'data' ? r.row.tenant_name : r.tenantName;
      return showDay ? name : <span data-testid="usage-row">{name}</span>;
    },
  };
  return [
    // `groupBy: 'tenant'` rows all carry the period's end date in `day` (core
    // `services/commercial/actions/usage.py`), so showing it would read as a
    // real per-row date rather than the grouping boundary it actually is.
    ...(showDay ? [dayColumn] : []),
    tenantColumn,
    {
      id: 'calls',
      header: t('header.calls'),
      accessorFn: (r) => (r.kind === 'data' ? r.row.calls : null),
      meta: { numeric: true },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span data-testid="calls">
            {r.kind === 'data' ? integer(r.row.calls, locale) : t('noReading')}
          </span>
        );
      },
    },
    {
      id: 'tenant_credits',
      header: () => <span data-unit="tenant">{t('header.tenantCredits')}</span>,
      accessorFn: (r) => (r.kind === 'data' ? r.row.tenant_credits : null),
      meta: { numeric: true },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span data-testid="tenant-credits">
            {r.kind === 'data' ? (
              <Credits value={r.row.tenant_credits} unit="tenant" showUnit />
            ) : (
              t('noReading')
            )}
          </span>
        );
      },
    },
    {
      id: 'cost',
      header: () => <span data-unit="provider">{t('header.cost')}</span>,
      accessorFn: (r) => (r.kind === 'data' ? r.row.provider_credits : null),
      meta: { numeric: true },
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind !== 'data') return <span data-testid="provider-credits">{t('noReading')}</span>;
        return (
          <div data-cost-group="">
            <CostGroup>
              <div className="flex flex-col items-end gap-0.5">
                <span data-testid="provider-credits">
                  <Credits value={r.row.provider_credits} unit="provider" showUnit />
                </span>
                {showTokens && (
                  <>
                    {/* Visibly labelled rather than moved behind `Hint`: a hovercard would
                        hide the split behind a hover/focus interaction on a figure this
                        screen's own acceptance treats as always-relevant beside the cost,
                        and the label costs one short word each. */}
                    <span className="flex items-baseline gap-1">
                      <span className="text-muted-foreground text-caption">
                        {t('tokens.input')}
                      </span>
                      <span
                        data-testid="input-tokens"
                        className="text-muted-foreground text-caption"
                      >
                        {r.row.input_tokens === null ? '—' : integer(r.row.input_tokens, locale)}
                      </span>
                    </span>
                    <span className="flex items-baseline gap-1">
                      <span className="text-muted-foreground text-caption">
                        {t('tokens.output')}
                      </span>
                      <span
                        data-testid="output-tokens"
                        className="text-muted-foreground text-caption"
                      >
                        {r.row.output_tokens === null ? '—' : integer(r.row.output_tokens, locale)}
                      </span>
                    </span>
                  </>
                )}
              </div>
            </CostGroup>
          </div>
        );
      },
    },
    {
      id: 'margin',
      header: t('header.margin'),
      accessorFn: (r) => (r.kind === 'data' ? r.row.tenant_credits - r.row.provider_credits : null),
      meta: { numeric: true },
      cell: ({ row }) => {
        const r = row.original;
        return r.kind === 'data' ? (
          <Margin tenantCredits={r.row.tenant_credits} providerCredits={r.row.provider_credits} />
        ) : (
          <span data-testid="margin">{t('noReading')}</span>
        );
      },
    },
    {
      id: 'margin_percent',
      header: t('header.marginPercent'),
      accessorFn: (r) =>
        r.kind === 'data' ? marginPercent(r.row.tenant_credits, r.row.provider_credits) : null,
      meta: { numeric: true },
      cell: ({ row }) => {
        const r = row.original;
        return r.kind === 'data' ? (
          <MarginPercent
            tenantCredits={r.row.tenant_credits}
            providerCredits={r.row.provider_credits}
          />
        ) : (
          <span data-testid="margin-percent">{t('noReading')}</span>
        );
      },
    },
  ];
}

export function Usage(): ReactNode {
  const [values, set] = useViewSearch('ops.usage', {
    period: { default: '30d', values: ['7d', '30d', '90d'] },
    groupBy: { default: 'day_tenant', values: ['tenant', 'day_tenant'] },
  });
  const period = values.period as UsagePeriod;
  const groupBy = values.groupBy as UsageGroupBy;
  const setPeriod = (next: UsagePeriod): void => set({ period: next });
  const setGroupBy = (next: UsageGroupBy): void => set({ groupBy: next });
  const range = periodToRange(period);
  const spendQuery = useOperatorSpend({ from: range.from, to: range.to, groupBy });
  const tenantsQuery = useOpsTenants();
  // Fetched independently of `spendQuery`: its own loading/error state never
  // blocks the table, and it is read here rather than inside `UsageBody` so
  // that state is visible even while the spend query is still pending.
  const platformQuery = usePlatformMargin({ from: range.from, to: range.to });
  const locale = getLocale();
  // `null` while pending or failed: the tenant list enriches the spend
  // answer and never gates it.
  const tenants = tenantsQuery.data ?? null;

  // In the table's own card header, beside the table (`158:4715`), rather
  // than a separate filter bar above it.
  const filterBar = (
    <FilterBar
      label={t('filters.label')}
      actions={
        <Badge variant="secondary" data-testid="usage-period-reading">
          {t('filters.period', {
            from: formatCalendarDay(range.from, locale),
            to: formatCalendarDay(range.to, locale),
          })}
        </Badge>
      }
    >
      <UsageControls
        period={period}
        groupBy={groupBy}
        onPeriodChange={setPeriod}
        onGroupByChange={setGroupBy}
      />
    </FilterBar>
  );

  // `UsageBody` mounts through pending and settled alike, never swapped out
  // for a full-page spinner: `filterBar` lives in the table's own header
  // (`DataTable`'s `header` prop), so the period and group-by controls have
  // to stay mounted through the load for the controls to be reachable
  // before the first response lands, not only after.
  if (spendQuery.status === 'error') {
    return (
      <Page>
        <PageHeader title={opsTitles.t('usage')} />
        <ErrorState failure={spendQuery.error.failure} onRetry={() => void spendQuery.refetch()} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title={opsTitles.t('usage')} />
      <UsageBody
        rows={spendQuery.data ?? []}
        loading={spendQuery.status === 'pending'}
        tenants={tenants}
        range={range}
        period={period}
        locale={locale}
        filterBar={filterBar}
        // `undefined` (pending or errored) and a settled `null` (nothing
        // spent in the period) both mean "nothing to show yet" here.
        platformMargin={platformQuery.data ?? null}
      />
    </Page>
  );
}

function UsageBody(props: {
  rows: OperatorSpendRow[];
  loading: boolean;
  tenants: OpsTenantRow[] | null;
  range: { from: string; to: string };
  period: UsagePeriod;
  locale: Locale;
  filterBar: ReactNode;
  platformMargin: PlatformMargin | null;
}): ReactNode {
  const { rows, loading, tenants, range, period, locale, filterBar, platformMargin } = props;
  const periodDays = { '7d': 7, '30d': 30, '90d': 90 }[period];

  const dayLevel = hasDayLevelData(rows, range.to);
  // Under the platform-admin widening the API returns no token counts at all,
  // so a column of dashes would say nothing.
  const showTokens = rows.some((row) => row.input_tokens !== null || row.output_tokens !== null);
  // `flexRender` treats a bare function as a component type, so rebuilding this array
  // every render remounts the whole table body (focus loss) even though nothing the
  // cells read has changed.
  const cols = useMemo(() => columns(locale, dayLevel, showTokens), [locale, dayLevel, showTokens]);
  const tableRows = useMemo(() => usageRows(rows, tenants, dayLevel), [rows, tenants, dayLevel]);

  const billed = rows.reduce((sum, row) => sum + row.tenant_credits, 0);
  const activeTenantCount = new Set(rows.map((row) => row.tenant_id)).size;
  // Narrowed here rather than at the JSX site so both tiles below share one
  // "is there a real figure" answer -- the api's own contract is that the
  // two fields are null together, but this reads that off the values rather
  // than assuming it.
  const platformFigure =
    platformMargin &&
    platformMargin.provider_credits !== null &&
    platformMargin.margin_credits !== null
      ? {
          providerCredits: platformMargin.provider_credits,
          marginCredits: platformMargin.margin_credits,
        }
      : null;

  return (
    <>
      {!loading && (
        <StatGrid>
          <StatCard
            label={t('summary.tenantCredits.label', { days: integer(periodDays, locale) })}
            value={<Credits value={billed} unit="tenant" showUnit testId="billed-total" />}
          />
          {platformFigure !== null && (
            <>
              <StatCard
                label={t('summary.platformCost.label')}
                value={
                  <Credits
                    value={platformFigure.providerCredits}
                    unit="provider"
                    showUnit
                    testId="platform-cost"
                  />
                }
                hint={t('summary.platform.hint')}
              />
              <StatCard
                label={t('summary.platformMargin.label')}
                value={
                  <Margin
                    // The api already computed the diff (`marginCredits`); this
                    // reproduces it through `Margin`'s own subtraction rather
                    // than adding a second way to format a margin.
                    tenantCredits={platformFigure.providerCredits + platformFigure.marginCredits}
                    providerCredits={platformFigure.providerCredits}
                    testId="platform-margin"
                  />
                }
                hint={t('summary.platform.hint')}
              />
            </>
          )}
          <StatCard
            label={t('summary.activeTenants.label')}
            value={<span data-testid="active-tenants">{integer(activeTenantCount, locale)}</span>}
            hint={
              tenants === null
                ? undefined
                : t('summary.activeTenants.hint', { total: integer(tenants.length, locale) })
            }
          />
        </StatGrid>
      )}

      <DataTable
        columns={cols}
        data={tableRows}
        loading={loading}
        caption={t('table.caption')}
        header={filterBar}
        emptyState={<EmptyState title={t('empty')} />}
      />
    </>
  );
}
